"use client";

import { Fragment } from "react";
import { motion } from "framer-motion";
import Link from "@/components/LocalizedLink";
import { trackEvent } from "@/lib/analytics";
import { useTranslation } from "@/lib/i18n";
import { accentInkStyle } from "@/lib/accent-ink";
import {
  CTA_SERVICE_TO_ID,
  popsFor,
  type ServiceId,
} from "@/data/service-availability";
import { ALL_POPS } from "@/lib/network-data";
import {
  leadTimeFor,
  formatLeadTimeCta,
} from "@/data/provisioning-lead-times";

export interface ServiceSpec {
  label: string;
  value: string;
}

export interface ServiceUseCase {
  title: string;
  description: string;
  icon: React.ReactNode;
}

export interface ServiceFeature {
  title: string;
  description: string;
}

export interface ServicePageData {
  title: string;
  subtitle: string;
  heroDescription: string;
  accentColor: string;
  icon: React.ReactNode;
  specs: ServiceSpec[];
  features: ServiceFeature[];
  useCases: ServiceUseCase[];
  ctaText: string;
  ctaService: string;
  /**
   * Optional standalone note rendered under the hero. Used on
   * /services/dark-fiber to carry the bilingual commercial-negotiation
   * note without deriving it from the routes data model.
   */
  footnote?: string;
  /**
   * When true, the layout renders its own headings and buttons in the
   * visitor's language. Pages opt in once their own copy is translated,
   * so a /bg/ page never mixes Bulgarian chrome with English content.
   * Opted in: /services/wavelengths-dwdm/.
   */
  bilingual?: boolean;
}

type ChromeLang = "en" | "bg";

const CHROME = {
  en: {
    configure: "Configure via NaaS",
    specs: "Technical Specifications",
    features: "Key Features",
    useCases: "Use Cases",
    coverageEyebrow: "Coverage",
    coverageTitle: "Available in these cities",
    coverageIpTransit:
      "Service delivery from Sofia (Equinix SO1/SO2, Telepoint) and Frankfurt (Equinix FR5, New Telco).",
    coverageDarkFibre:
      "Dark Fiber in service today in the Sofia metro. National and cross-border corridors on the roadmap (Sofia–Kalotina mid-2027; Route A to Komotini Q1 2028) — route-level enquiry only.",
    coverageAll: (n: number) => `Service delivery across all ${n} Sofia Connect PoPs.`,
    ctaTitle: "Ready to Get Started?",
    ctaBody: "Our network engineers will design the optimal solution for your requirements.",
    ctaFallbackLead: "Typical provisioning takes 5–10 business days.",
    viewAll: "View All Services",
  },
  bg: {
    configure: "Конфигурирайте през NaaS",
    specs: "Технически параметри",
    features: "Основни възможности",
    useCases: "Приложения",
    coverageEyebrow: "Покритие",
    coverageTitle: "Налично в тези градове",
    coverageIpTransit:
      "Услугата се доставя от София (Equinix SO1/SO2, Telepoint) и Франкфурт (Equinix FR5, New Telco).",
    coverageDarkFibre:
      "Тъмното влакно е в експлоатация в София. Националните и трансграничните коридори са в плановете (София–Калотина в средата на 2027 г., маршрут A до Комотини през първото тримесечие на 2028 г.) и се обсъждат само при запитване за конкретен маршрут.",
    coverageAll: (n: number) => `Услугата се доставя от всички ${n} точки на присъствие на Sofia Connect.`,
    ctaTitle: "Да обсъдим вашия проект",
    ctaBody: "Нашите мрежови инженери ще проектират решение според изискванията ви.",
    ctaFallbackLead: "Типичното въвеждане в експлоатация отнема 5–10 работни дни.",
    viewAll: "Всички услуги",
  },
} as const;

/* Bulgarian names for the PoPs shown in the coverage list. */
const CITY_BG: Record<string, string> = {
  frankfurt: "Франкфурт",
  istanbul: "Истанбул",
  athens: "Атина",
  london: "Лондон",
  tbilisi: "Тбилиси",
  dubai: "Дубай",
  yerevan: "Ереван",
  agarak: "Агарак",
  sofia: "София",
  plovdiv: "Пловдив",
  varna: "Варна",
  burgas: "Бургас",
  "stara-zagora": "Стара Загора",
  balchik: "Балчик",
  dobrich: "Добрич",
  pleven: "Плевен",
  montana: "Монтана",
  blagoevgrad: "Благоевград",
};

/* Spec values sit in narrow cards (two columns at 360px). Size the value by
 * its longest word so words like "Deterministic" or "Детерминирана" fit,
 * and keep short tokens such as "1G–400G" on one line. Long words may carry
 * soft hyphens (U+00AD) in the page data as a last-resort break point. */
function specValueClass(value: string): string {
  const words = value.replace(/\u00ad/g, "").split(/\s+/);
  // Cyrillic glyphs in the heading font run about a quarter wider than Latin.
  const width = (w: string) => [...w].reduce((n, ch) => n + (/[\u0400-\u04ff]/.test(ch) ? 1.25 : 1), 0);
  const longest = Math.max(...words.map(width));
  const keepWhole = words.length === 1 ? " whitespace-nowrap" : "";
  if (longest <= 8) return `text-2xl sm:text-3xl${keepWhole}`;
  if (longest <= 10) return "text-lg sm:text-2xl";
  if (longest <= 13) return "text-base sm:text-xl";
  return "text-sm sm:text-lg";
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 },
  }),
};

export function ServicePageLayout({ data }: { data: ServicePageData }) {
  const { language } = useTranslation();
  const lang: ChromeLang = data.bilingual && language === "bg" ? "bg" : "en";
  const c = CHROME[lang];
  return (
    <main className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 network-grid opacity-30" />
        <div
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full blur-[120px] opacity-15 pointer-events-none"
          style={{ backgroundColor: data.accentColor }}
        />

        <div className="relative z-10 mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center accent-ink"
                style={accentInkStyle(data.accentColor, { backgroundColor: `${data.accentColor}15` })}
              >
                {data.icon}
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-eyebrow">
                {data.subtitle}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
              {data.title}
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              {data.heroDescription}
            </p>
            {data.footnote ? (
              <p
                role="note"
                className="mt-5 max-w-2xl text-sm text-muted-foreground/80 italic border-l-2 border-primary/40 pl-3"
              >
                {data.footnote}
              </p>
            ) : null}

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              {data.ctaText ? (
              <Link
                href={`/contact?department=sales&service=${data.ctaService}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium btn-primary rounded-xl transition-colors glow-teal"
              >
                {data.ctaText}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Link>
              ) : null}
              <Link
                href="/#configurator"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium border border-border text-foreground rounded-xl hover:bg-surface-light transition-colors"
              >
                {c.configure}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Technical Specs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={0}
            className="text-2xl sm:text-3xl font-bold text-foreground mb-12 text-center"
          >
            {c.specs}
          </motion.h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {data.specs.map((spec, i) => (
              <motion.div
                key={spec.label}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                // .bento-card is unlayered CSS with 2rem padding, so the
                // narrower mobile padding needs the important modifier.
                className="bento-card text-center px-3! sm:px-6! lg:px-8!"
              >
                <p
                  className={`${specValueClass(spec.value)} font-bold mb-1 accent-ink`}
                  style={accentInkStyle(data.accentColor)}
                >
                  {/* Hyphenated words such as E-Line or Q-in-Q stay whole; other
                   * words may still break at a soft hyphen. */}
                  {spec.value.split(" ").map((w, k) => (
                    <Fragment key={k}>
                      {k > 0 ? " " : null}
                      {w.includes("-") ? <span className="whitespace-nowrap">{w}</span> : w}
                    </Fragment>
                  ))}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {spec.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="mx-auto max-w-5xl">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={0}
            className="text-2xl sm:text-3xl font-bold text-foreground mb-12 text-center"
          >
            {c.features}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="bento-card"
              >
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="mx-auto max-w-5xl">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={0}
            className="text-2xl sm:text-3xl font-bold text-foreground mb-12 text-center"
          >
            {c.useCases}
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.useCases.map((uc, i) => (
              <motion.div
                key={uc.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="bento-card"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 accent-ink"
                  style={accentInkStyle(data.accentColor, { backgroundColor: `${data.accentColor}15` })}
                >
                  {uc.icon}
                </div>
                <h3 className="text-base font-semibold text-foreground mb-2">
                  {uc.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {uc.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Cities — driven by src/data/service-availability.ts
         so IP Transit / L2VPN / DWDM / Dark Fiber each show only the
         PoPs where they are actually deliverable. */}
      {(() => {
        const serviceId: ServiceId | undefined = CTA_SERVICE_TO_ID[data.ctaService];
        const availableIds = serviceId ? new Set(popsFor(serviceId)) : new Set<string>();
        // PoPs with a per-city landing page get a linked chip. The others
        // (e.g. Frankfurt) still deliver the service, so they are listed as
        // plain chips. Skipping them left IP Transit showing only "Sofia"
        // under a sentence that names Sofia and Frankfurt.
        const HAS_CITY_PAGE = new Set<string>([
          "sofia", "plovdiv", "varna", "burgas", "stara-zagora",
          "balchik", "dobrich", "pleven", "montana", "blagoevgrad",
        ]);
        const cities = ALL_POPS
          .filter((p) => availableIds.has(p.id))
          .map((p) => ({
            slug: p.id,
            label: lang === "bg" ? CITY_BG[p.id] ?? p.city : p.city,
            hasPage: HAS_CITY_PAGE.has(p.id),
          }));
        // Coverage sentence adapts to reality. Kept factual, no marketing.
        const coverage =
          serviceId === "ip-transit"
            ? c.coverageIpTransit
            : serviceId === "dark-fibre"
              ? c.coverageDarkFibre
              : c.coverageAll(availableIds.size);
        return (
          <section className="py-14 px-4 sm:px-6 lg:px-8 border-t border-border">
            <div className="mx-auto max-w-5xl">
              <div className="text-center mb-8">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  {c.coverageEyebrow}
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                  {c.coverageTitle}
                </h2>
                <p className="mt-3 text-muted-foreground text-sm sm:text-base">
                  {coverage}
                </p>
              </div>
              {cities.length > 0 && (
                <div
                  className={
                    // A short list is centred under the centred heading; a
                    // full row or more keeps the five-column grid.
                    cities.length < 5
                      ? "flex flex-wrap justify-center gap-3"
                      : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3"
                  }
                >
                  {cities.map((city) => city.hasPage ? (
                    <Link
                      key={city.slug}
                      href={`/locations/${city.slug}/`}
                      onClick={() =>
                        trackEvent("service_page_city_click", {
                          city: city.slug,
                          service: data.ctaService,
                        })
                      }
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-border bg-surface text-sm text-foreground hover:border-accent/40 hover:bg-surface-light hover:text-accent transition-all"
                    >
                      <span>{city.label}</span>
                      <svg className="w-3.5 h-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  ) : (
                    <span
                      key={city.slug}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-border bg-surface text-sm text-foreground"
                    >
                      {city.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* Bottom CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="mx-auto max-w-3xl text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={0}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              {c.ctaTitle}
            </h2>
            <p className="text-muted-foreground mb-8">
              {c.ctaBody}{" "}
              {(() => {
                // Resolve service id from ctaService slug; fall back to the
                // generic sentence only if the slug is unknown. All four
                // productised services resolve.
                const sid: ServiceId | undefined = CTA_SERVICE_TO_ID[data.ctaService];
                return sid
                  ? formatLeadTimeCta(leadTimeFor(sid), lang)
                  : c.ctaFallbackLead;
              })()}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {data.ctaText ? (
              <Link
                href={`/contact?department=sales&service=${data.ctaService}`}
                className="inline-flex items-center gap-2 px-8 py-3 text-sm font-medium btn-primary rounded-xl transition-colors glow-teal"
              >
                {data.ctaText}
              </Link>
              ) : null}
              <Link
                href="/#services"
                onClick={() =>
                  trackEvent("service_view_all_services", { from_service: data.ctaService })
                }
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border border-border text-foreground rounded-xl hover:bg-surface-light transition-colors"
              >
                {c.viewAll}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
